import React from 'react'
import { AppContextType } from 'next/dist/shared/lib/utils'
import { getVersion } from '../lib/withVersion'
import '../style.scss'
import { AppProps } from 'next/app'
import 'regenerator-runtime/runtime'
import Script from 'next/script'
import { DefaultSeo } from 'next-seo'
import { clarity } from 'react-microsoft-clarity'
import { GoogleAnalytics } from 'nextjs-google-analytics'

import { AppProvider } from './../state/app-store'
import { MantineProvider, MantineThemeOverride } from '@mantine/core'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/carousel/styles.css'
import '@mantine/notifications/styles.css'

import { ApolloProvider } from '@apollo/client'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'

import { Inter } from 'next/font/google'
import NextNProgress from 'nextjs-progressbar'

import { useApollo } from '../lib/apollo-client'
import CheckToken from '../components/CheckToken'
import VersionContext from '../contexts/VersionContext'
import * as Types from '../declarations'
import { withVersion } from '../lib/withVersion'

interface Props extends AppProps {
	// per-request site version resolved from the request host (issue #123) —
	// NEXT_PUBLIC_SITE_VERSION isn't reliably inlined in every build
	initialVersion?: Types.VERSION
}

const interFont = Inter({
	weight: ['400'],
	subsets: ['latin'],
	display: 'swap',
	variable: '--font-inter',
})

export const trekkingColours = [
	'#0e4868',
	'#105279',
	'#135b89',
	'#15659a',
	'#1d77ab',
	'#2688bd',
	'#2e9ace',
	'#5ab3db',
	'#86cce7',
	'#b2e5f4',
]
export const peaksColours = [
	'#912928',
	'#a22d27',
	'#b23027',
	'#c33426',
	'#d1452f',
	'#df5738',
	'#ed6841',
	'#f38954',
	'#f9ab68',
	'#ffcc7b',
]

// export const PRIMARY_COLOURS =
// 	process.env.NEXT_PUBLIC_SITE_VERSION === Types.VERSION.TREKKING
// 		? ['#0E4868', '#15659A', '#2E9ACE', '#B2E5F4']
// 		: ['#912928', '#C33426', '#ED6841', '#FFCC7B']
const isTrekking =
	process.env.NEXT_PUBLIC_SITE_VERSION === Types.VERSION.TREKKING
export const THEME_COLOURS = (isTrekking ? trekkingColours : peaksColours) as [
	string,
	string,
	string,
	string,
	string,
	string,
	string,
	string,
	string,
	string,
	...string[]
]

// DO NOT CHANGE THIS - MUST STAY AT OR CLOSE TO MANTINE DEFAULT SHADE OR OTHER COLOURS GET MESSED UP
export const ACCENT_NO = 4
export const ACCENT_COLOUR = THEME_COLOURS[ACCENT_NO]

const MyApp = ({ Component, pageProps, initialVersion }: Props) => {
	const client = useApollo()
	const version = process.env.NEXT_PUBLIC_SITE_VERSION as Types.VERSION

	React.useEffect(() => {
		clarity.init(
			(process?.env?.NEXT_PUBLIC_SITE_VERSION as Types.VERSION) ===
				Types.VERSION.TREKKING
				? 'p9kin2s5sa'
				: 'ot8dzpegxn',
		)
	}, [])

	const seo = {
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
	}

	const theme: MantineThemeOverride = {
		primaryShade: ACCENT_NO,
		// primaryShade: {
		// 	// @ts-ignore
		// 	light: 2,
		// 	// dark: 9,
		// },
		colors: {
			primary: THEME_COLOURS,
			brand: THEME_COLOURS,
		},
		fontFamily: 'Inter, sans-serif',
		headings: {
			fontFamily: 'Geologica, sans-serif',
		},
		components: {
			Timeline: {
				defaultProps: {
					color: 'brand',
				},
			},
			Progress: {
				defaultProps: {
					color: 'brand',
				},
			},
			Loader: {
				defaultProps: {
					color: 'brand',
				},
			},
			NavLink: {
				defaultProps: {
					color: 'brand',
				},
			},
			Badge: {
				defaultProps: {
					color: 'brand',
				},
			},
			Slider: {
				defaultProps: {
					color: 'brand',
				},
			},
			Spoiler: {
				defaultProps: {
					color: 'brand',
				},
			},
			Anchor: {
				defaultProps: {
					color: 'brand',
				},
			},
			ActionIcon: {
				defaultProps: {
					color: 'brand',
					// Mantine 6 default look: transparent bg, brand-colored icon
					variant: 'transparent',
				},
				// v9 transparent variant resolves icon colour to brand-text
				// (shade 9 = pale yellow); v6 default was primaryShade (red)
				vars: (_theme: unknown, props: any) =>
					!props.variant || props.variant === 'transparent'
						? { root: { '--ai-color': THEME_COLOURS[ACCENT_NO] } }
						: {},
			},
			Alert: {
				// Mantine 9: selector-based theme `styles` no longer apply; use CSS vars.
				// Replicates v6 prod look: light = THEME_COLOURS[9] bg, THEME_COLOURS[2] text;
				// outline = white bg + white text; filled = THEME_COLOURS[2] text.
				vars: (_theme: unknown, props: any) => {
					const variant = props.variant ?? 'light'
					if (variant === 'outline')
						return {
							root: {
								'--alert-bg': '#fff',
								'--alert-color': '#fff',
							},
						}
					if (variant === 'light')
						return {
							root: {
								'--alert-bg': THEME_COLOURS[9],
								'--alert-color': THEME_COLOURS[2],
							},
						}
					return { root: { '--alert-color': THEME_COLOURS[2] } }
				},
			},

			Checkbox: {
				defaultProps: {
					color: 'brand',
				},
			},
			// Label: {
			// 	styles: {
			// 		icon: {
			// 			color: THEME_COLOURS[2],
			// 		},
			// 	},
			// },
			Button: {
				defaultProps: {
					color: 'brand',
				},
				// Mantine 9: v6 `variants` API removed; replicate white text on light variant
				vars: (_theme: unknown, props: any) =>
					(props.variant ?? 'filled') === 'light'
						? { root: { '--button-color': '#fff' } }
						: {},
			},
			Rating: {
				defaultProps: {
					color: 'brand',
				},
			},
			Pagination: {
				defaultProps: {
					color: 'brand',
				},
			},
			Chip: {
				defaultProps: {
					color: 'brand',
				},
				vars: (_theme: unknown, props: any) =>
					(props.variant ?? 'light') === 'light'
						? { root: { '--chip-color': '#fff' } }
						: {},
			},
		},
	}

	return (
		<main className={`${interFont.variable}`}>
			<NextNProgress color={ACCENT_COLOUR} options={{ showSpinner: false }} />
			<ApolloProvider client={client}>
				<MantineProvider
					forceColorScheme="light"
					theme={{
						...theme,
						primaryColor: 'brand',
					}}
				>
					<AppProvider>
						{/* Your Plausible Analytics script */}
						<Script
							src="https://plausible.io/js/script.hash.outbound-links.pageview-props.tagged-events.js"
							data-domain={
								(process?.env?.NEXT_PUBLIC_SITE_VERSION as Types.VERSION) ===
								Types.VERSION.TREKKING
									? 'guidedtrekking.com'
									: 'guidedpeaks.com'
							}
							strategy="lazyOnload"
						/>
						<Script id="plausible-init" strategy="lazyOnload">
							{`window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }`}
						</Script>

						{
							<VersionContext initialVersion={initialVersion}>
								<DefaultSeo
									openGraph={{
										type: 'website',
										locale: 'en_US',
										siteName: seo.siteName,
									}}
								/>
								<ModalsProvider>
									<GoogleAnalytics
										trackPageViews
										gaMeasurementId={process.env.NEXT_PUBLIC_GA_ID as string}
									/>
									<Component {...pageProps} />
								</ModalsProvider>
								<CheckToken />
								<Notifications position="top-right" />
							</VersionContext>
						}
					</AppProvider>
				</MantineProvider>
			</ApolloProvider>
		</main>
	)
}

// App-level getInitialProps keeps non-SSG pages dynamic (parity with the old
// withReduxStore HOC). Without it Next prerenders every file under pages/ —
// including pages/**/components/* helper files — which crashes on undefined props.
// Pages with getStaticProps are unaffected and still prerender.
MyApp.getInitialProps = async (appContext: AppContextType) => ({
	// SSR the correct site variant per request host — the version context
	// (header logo, branding) otherwise falls back to TREKKING in raw HTML
	initialVersion: getVersion(appContext.ctx?.req?.headers?.host),
})

export default MyApp
